<template>
  <div id="modern-agent-grid" class="grid-space-4">
    <div 
      v-for="agent in agents" 
      :key="agent.agentid" 
      class="modern-agent-card group"
      @click="$emit('navigate', agent)"
    >
      <div class="card-inner">
        <div class="card-header">
          <div class="avatar-wrapper" :class="getIconContainerClass(agent)">
            <img v-if="getIcon(agent) !== '' && agent.agenttype !== 'HelpAgent'" :src="getIcon(agent)" class="avatar-img" alt="" />
            <Icon v-else :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'mingcute:ai-fill'" class="avatar-icon" :class="getIconColorClass(agent)" />
          </div>
          <div class="status-indicator" :class="agent.agenttype === 'HelpAgent' ? 'status-system' : 'status-active'"></div>
        </div>
        
        <div class="card-content">
          <h3 class="agent-name" dir="auto" :title="agent.agentname" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ agent.agentname }}</h3>
          <p class="agent-desc" :title="agent.agentShortDescription" :class="isHebrew === true ? 'text-right' : 'text-left'">
            {{ agent.agentShortDescription !== undefined && agent.agentShortDescription !== null && agent.agentShortDescription.length > 0 ? agent.agentShortDescription : t('dashboard.noDescription') }}
          </p>
          <div v-if="showModelLabel === true && resolveModelLabel(agent) !== ''" class="agent-model-row" :class="isHebrew === true ? 'text-right' : 'text-left'">
            <span class="agent-model-label">{{ t('aiChatAgentSetup.agentInformation.llmModelLabel', 'LLM Model') }}</span>
            <span class="agent-model-value">{{ resolveModelLabel(agent) }}</span>
          </div>
          <div v-if="resolveChannelIcons(agent).length > 0" class="mt-3 flex flex-wrap items-center" :class="isHebrew === true ? 'justify-end' : ''">
            <span v-for="channel in resolveChannelIcons(agent)" :key="channel.id" class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300" :class="isHebrew === true ? 'ml-2' : 'mr-2'" :title="channel.label">
              <Icon :icon="channel.icon" width="14" />
            </span>
          </div>
        </div>

        <div class="card-footer">
          <div class="footer-left">
            <span class="last-active">{{ agent.agenttype === 'HelpAgent' ? t('dashboard.alwaysAvailable') : t('dashboard.lastActive') + ' ' + formatDate(agent.updatedat) }}</span>
          </div>
          <div class="footer-right flex items-center">
            <template v-if="showEdit === true">
              <button @click.stop="$emit('openChat', agent)" class="action-btn btn-chat mr-2">
                <span>{{ resolveOpenButtonLabel(agent) }}</span>
              </button>
              <button v-if="resolveShowVoiceOpenButton(agent) === true" @click.stop="$emit('openVoice', agent)" class="action-btn btn-secondary mr-2">
                <span>{{ t('aiChats.openVoiceButton', 'Open Voice') }}</span>
              </button>
              <button @click.stop="$emit('edit', agent)" class="action-btn btn-secondary">
                <span>{{ t('aiChats.editButton', 'Edit') }}</span>
              </button>
            </template>
            <button v-else @click.stop="handlePrimaryAction(agent)" class="action-btn" :class="agent.agenttype === 'HelpAgent' ? 'btn-help' : 'btn-chat'">
              <span>{{ resolvePrimaryButtonLabel(agent) }}</span>
              <Icon :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'tabler:arrow-right'" class="btn-icon" :class="isHebrew === true ? 'mr-2' : 'ml-2'" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n.js';

const props = defineProps({
  agents: {
    type: Array,
    required: true
  },
  isHebrew: {
    type: Boolean,
    default: false
  },
  styleLogos: {
    type: Object,
    default: () => ({})
  },
  showEdit: {
    type: Boolean,
    default: false
  },
  showModelLabel: {
    type: Boolean,
    default: true
  },
  getOpenButtonLabel: {
    type: Function,
    default: null
  },
  getChannelIcons: {
    type: Function,
    default: null
  },
  getShowVoiceOpenButton: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['navigate', 'edit', 'openChat', 'openVoice']);
const { t } = useI18n();

function normalizeStyleGuid(agent) {
  if (agent.styleguid !== undefined && agent.styleguid !== null && agent.styleguid !== '') return agent.styleguid;
  if (agent.styleGuid !== undefined && agent.styleGuid !== null && agent.styleGuid !== '') return agent.styleGuid;
  if (agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.styleGuid !== undefined && agent.agentdata.styleGuid !== null && agent.agentdata.styleGuid !== '') return agent.agentdata.styleGuid;
  return '';
}

function getIcon(agent) {
  const agentId = agent !== undefined && agent !== null && agent.agentid !== undefined && agent.agentid !== null ? String(agent.agentid) : '';
  if (agentId !== '' && props.styleLogos !== undefined && props.styleLogos !== null && props.styleLogos[agentId] !== undefined && props.styleLogos[agentId] !== null && props.styleLogos[agentId] !== '') {
    return props.styleLogos[agentId];
  }
  const guid = normalizeStyleGuid(agent);
  if (guid === '') return '';
  if (props.styleLogos !== undefined && props.styleLogos !== null && props.styleLogos[guid] !== undefined && props.styleLogos[guid] !== null) {
    return props.styleLogos[guid];
  }
  return '';
}

function getIconContainerClass(agent) {
  if (getIcon(agent) !== '' && agent.agenttype !== 'HelpAgent') return 'bg-white shadow-sm';
  if (agent.agenttype === 'HelpAgent') return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-indigo-50 dark:bg-indigo-900/20';
}

function getIconColorClass(agent) {
  if (agent.agenttype === 'HelpAgent') return 'text-amber-500';
  return 'text-indigo-500';
}

function formatDate(dateString) {
  if (dateString === undefined || dateString === null) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return 'Recently';
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

function resolveOpenButtonLabel(agent) {
  if (typeof props.getOpenButtonLabel === 'function') {
    const resolved = props.getOpenButtonLabel(agent);
    if (typeof resolved === 'string' && resolved.trim() !== '') {
      return resolved;
    }
  }
  return t('aiChatAgentSetup.buttons.openAgent', 'Open Agent');
}

function resolvePrimaryButtonLabel(agent) {
  if (agent !== null && agent !== undefined && agent.agenttype === 'HelpAgent') {
    return t('dashboard.getHelp');
  }
  if (typeof props.getOpenButtonLabel === 'function') {
    const resolved = props.getOpenButtonLabel(agent);
    if (typeof resolved === 'string' && resolved.trim() !== '') {
      return resolved;
    }
  }
  return t('dashboard.startChat');
}

function handlePrimaryAction(agent) {
  if (typeof props.getOpenButtonLabel === 'function') {
    emit('openChat', agent);
    return;
  }
  emit('navigate', agent);
}

function resolveChannelIcons(agent) {
  if (typeof props.getChannelIcons === 'function') {
    const resolved = props.getChannelIcons(agent);
    if (Array.isArray(resolved) === true) {
      return resolved;
    }
  }
  return [];
}

function resolveShowVoiceOpenButton(agent) {
  if (typeof props.getShowVoiceOpenButton === 'function') {
    return props.getShowVoiceOpenButton(agent) === true;
  }
  return false;
}

function resolveModelLabel(agent) {
  if (agent !== null && agent !== undefined && typeof agent.modelLabel === 'string' && agent.modelLabel.trim() !== '') {
    return agent.modelLabel.trim();
  }
  if (agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && typeof agent.agentdata.selectedLlmModelId === 'string' && agent.agentdata.selectedLlmModelId.trim() !== '') {
    return agent.agentdata.selectedLlmModelId.trim();
  }
  if (agent !== null && agent !== undefined && typeof agent.selectedLlmModelId === 'string' && agent.selectedLlmModelId.trim() !== '') {
    return agent.selectedLlmModelId.trim();
  }
  return '';
}
</script>

<style scoped>
#modern-agent-grid {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  width: 100%;
  padding: 0;
}

.modern-agent-card {
  box-sizing: border-box;
  flex: 0 0 calc(100% - 1rem);
  min-width: 0;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.dark .modern-agent-card {
  background-color: #0f172a;
  border-color: #1e293b;
}

.modern-agent-card:hover {
  border-color: #4f46e5;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
}

.card-inner {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  position: relative;
}

.avatar-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.avatar-img {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 12px;
}

.avatar-icon {
  width: 28px;
  height: 28px;
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  border: 2px solid #ffffff;
  position: absolute;
  top: 42px;
  left: 42px;
}

.dark .status-indicator {
  border-color: #0f172a;
}

.status-active { background-color: #10b981; }
.status-system { background-color: #f59e0b; }

.agent-name {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  margin-bottom: 0.375rem;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color 0.2s ease;
}

.dark .agent-name {
  color: #f8fafc;
}

.card-content {
  min-width: 0;
}

.group:hover .agent-name {
  color: #4f46e5;
}

.agent-desc {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.75rem;
  overflow-wrap: anywhere;
}

.dark .agent-desc {
  color: #94a3b8;
}

.agent-model-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: -0.5rem;
  margin-bottom: 1.25rem;
}

.agent-model-label {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 600;
  margin-inline-end: 0.4rem;
}

.agent-model-value {
  font-size: 0.75rem;
  color: #374151;
  background-color: #f3f4f6;
  border-radius: 9999px;
  padding: 0.25rem 0.6rem;
  font-weight: 600;
}

.dark .agent-model-value {
  color: #e5e7eb;
  background-color: #374151;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.dark .card-footer {
  border-top-color: #1e293b;
}

.last-active {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-btn {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-chat {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.dark .btn-chat {
  background-color: #78350f;
  color: #fde68a;
  border-color: #b45309;
}

.btn-chat:hover {
  background-color: #fde68a;
  color: #78350f;
}

.dark .btn-chat:hover {
  background-color: #b45309;
  color: #ffffff;
}

.btn-secondary {
  background-color: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.dark .btn-secondary {
  background-color: #1e293b;
  color: #94a3b8;
  border-color: #334155;
}

.btn-secondary:hover {
  background-color: #ffffff;
  color: #0f172a;
  border-color: #cbd5e1;
}

.btn-help {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.dark .btn-help {
  background-color: #78350f;
  color: #fde68a;
  border-color: #b45309;
}

.btn-help:hover {
  background-color: #fde68a;
  color: #78350f;
}

.dark .btn-help:hover {
  background-color: #b45309;
  color: #ffffff;
}

.btn-icon {
  width: 16px;
  height: 16px;
}
</style>







